import { create } from 'zustand';
import { streamConciergeChat, fetchConciergeHistory, type ConciergeMessage, type ConciergeVenue } from '@/lib/api/concierge';
import { venuesApi } from '@/lib/api/venues';
import { getVenuePrimaryImageUrl, getVenueImageGalleryUrls } from '@/lib/venueImages';
import { reverseGeocodeCity } from '@/lib/googleCityAutocomplete';
import type { Venue } from '@/types/api';

export interface ConciergeTagChip {
  id: number;
  name: string;
}

// ─── State shape ────────────────────────────────────────────────────────────

export interface ConciergeChatMessage extends ConciergeMessage {
  id: string;
  venues?: ConciergeVenue[];
  isStreaming?: boolean;
  isError?: boolean;
}

const GREETINGS: Record<string, string> = {
  lucy: "Welcome! I'm Lucy, your personal curator. Ask me to find you something refined — fine dining, wine bars, elevated lounges.",
  ace: "Yo, Ace here. Tell me what kind of night you're chasing and I'll find the spot.",
  dan: "Hey, Dan here. Looking for a power dinner or a networking-friendly venue? Just ask.",
  olivia: "Hi! I'm Olivia. Tell me about your event and I'll help you find the right venue.",
  default:
    "Hi there! I'm Luna, your AI concierge. Ask me to find venues — try \"rooftop bars in Manhattan\" or \"best spot for a birthday party\".",
};

/** Loose match for messages implying proximity — the trigger for requesting geolocation. */
const NEAR_ME_PATTERN = /\b(near me|nearby|close by|around me|walking distance|around here|close to me)\b/i;

/** Wraps the callback-based Geolocation API in a promise with a timeout; null on any failure
 *  (denied, unsupported, timed out) — callers treat that as "no location," never an error. */
function requestBrowserLocation(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => resolve(null),
      { timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  });
}

function makeId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Maps the REST `Venue` shape (used by /vl/filter, /vl/venues) onto the same
 *  `ConciergeVenue` shape the AI chat's tool results already use, so Explore results
 *  render through the exact same card/map/hover components as chat results. */
export function venueToConciergeVenue(v: Venue): ConciergeVenue {
  const gallery = getVenueImageGalleryUrls(v);
  const primary = getVenuePrimaryImageUrl(v);
  return {
    id: v.id,
    name: v.name,
    slug: v.slug,
    logo: primary || null,
    // `Venue`'s TS type doesn't declare `city`/`location` (the API response has `city` — same
    // DB column used elsewhere in this session's backend work; `location` isn't a real Venue
    // field, just a defensive display fallback), so read both defensively.
    address: v.address ?? (v as unknown as { location?: string }).location ?? null,
    city: (v as unknown as { city?: string }).city ?? v.state ?? null,
    state: v.state ?? null,
    location: (v as unknown as { location?: string }).location ?? null,
    latitude: v.latitude !== undefined && v.latitude !== null ? Number(v.latitude) : null,
    longitude: v.longitude !== undefined && v.longitude !== null ? Number(v.longitude) : null,
    tags: (v.tags ?? []).map((t) => t.name),
    images: gallery.length > 0 ? gallery : primary ? [primary] : undefined,
  };
}

export type ConciergeTab = 'chat' | 'explore';

interface ConciergeState {
  messages: ConciergeChatMessage[];
  isSending: boolean;
  character: string | null;
  /** Hovered venue id, synced between the card strip and the map — on the widget
   *  they're siblings in the same component, but on the maximized page the map is
   *  a separate column, so this lives in the shared store rather than local state. */
  hoveredVenueId: string | null;

  /** Chat vs. category-browse — Explore is plain REST (no AI/LLM call, no token cost). */
  activeTab: ConciergeTab;
  /** "Venue Type" tag bucket, fetched once and cached — the chip source. */
  venueTagChips: ConciergeTagChip[] | null;
  venueTagChipsLoading: boolean;
  exploreCategoryId: number | null;
  exploreVenues: ConciergeVenue[];
  exploreLoading: boolean;
  /** Guards `loadPersistedHistory` to run once per page load, not once per mounted surface
   *  (widget + maximized page could both call it). */
  historyLoaded: boolean;
  /** Browser geolocation, requested lazily the first time a message implies proximity —
   *  never persisted, cached in memory for the rest of this page session so it doesn't
   *  re-prompt on every "near me" message. `undefined` = not yet requested/resolved this
   *  session (distinct from `null` = requested and denied/unavailable). */
  guestLocation: { lat: number; lng: number } | null | undefined;

  /** Full-page /chat only: the city driving the default map view + search bias. `undefined`
   *  = not yet attempted; `null` = attempted and unresolved (denied/unavailable, no city
   *  chosen yet) — the map shows a "search for a city" prompt in that state. */
  mapCity: { name: string; lat: number; lng: number } | null | undefined;
  cityPickerOpen: boolean;
  /** Venues near `mapCity`, shown on the default map/list before any chat/explore results
   *  exist — populated automatically once a city resolves, not just a blank "you are here" dot. */
  nearbyVenues: ConciergeVenue[];
  nearbyLoading: boolean;
  /** Toggles the right panel between the map and a scrollable card list — mirrors /search/map's
   *  List/Map toggle. Applies to whichever venue set is currently showing (nearby/chat/explore). */
  mapViewMode: 'map' | 'list';

  /** Seeds the greeting once per character; a no-op if messages already exist. */
  ensureGreeting: (character: string | null) => void;
  /** Loads the logged-in member's saved conversation, if any, to resume it instead of
   *  starting fresh — no-ops for guests (empty result) or if already loaded/messages exist. */
  loadPersistedHistory: () => Promise<void>;
  setCharacter: (character: string | null) => void;
  sendMessage: (text: string) => Promise<void>;
  setHoveredVenue: (id: string | null) => void;
  setActiveTab: (tab: ConciergeTab) => void;
  /** Fetches the venue-type tag chips once; no-ops on repeat calls. */
  loadVenueTagChips: () => Promise<void>;
  /** null = "All". Fetches and maps matching venues into exploreVenues. */
  selectExploreCategory: (tagId: number | null) => Promise<void>;
  /** Attempts browser geolocation + reverse geocoding to seed the default map city.
   *  No-ops if a city is already set (from this or the picker) or already attempted. */
  initMapCity: () => Promise<void>;
  setMapCity: (city: { name: string; lat: number; lng: number }) => void;
  setCityPickerOpen: (open: boolean) => void;
  /** Fetches venues in a bounding box around the given coordinates into `nearbyVenues`. */
  loadNearbyVenues: (lat: number, lng: number) => Promise<void>;
  setMapViewMode: (mode: 'map' | 'list') => void;
  /** Aborts any in-flight stream — call on unmount of whichever surface initiated it. */
  cancel: () => void;
}

let abortController: AbortController | null = null;

// ─── Store ──────────────────────────────────────────────────────────────────

/**
 * Shared conversation state for the concierge chat — used by BOTH the floating
 * widget (`AIConcierge.tsx`) and the full-page maximized view (`ConciergeMaximize.tsx`)
 * so switching between them carries the conversation over instead of resetting it.
 */
export const useConciergeStore = create<ConciergeState>((set, get) => ({
  messages: [],
  isSending: false,
  character: null,
  hoveredVenueId: null,
  activeTab: 'chat',
  venueTagChips: null,
  venueTagChipsLoading: false,
  exploreCategoryId: null,
  exploreVenues: [],
  exploreLoading: false,
  historyLoaded: false,
  guestLocation: undefined,
  mapCity: undefined,
  cityPickerOpen: false,
  nearbyVenues: [],
  nearbyLoading: false,
  mapViewMode: 'map',

  setMapViewMode: (mode) => set({ mapViewMode: mode }),

  setMapCity: (city) => {
    set({ mapCity: city, cityPickerOpen: false });
    void get().loadNearbyVenues(city.lat, city.lng);
  },

  setCityPickerOpen: (open) => set({ cityPickerOpen: open }),

  loadNearbyVenues: async (lat, lng) => {
    set({ nearbyLoading: true });
    try {
      // ~0.15° a side (roughly a 15-20km box) — a reasonable "nearby" default view,
      // not a precise radius search (that's what search_venues's nearMe is for in chat).
      const bounds = `${lat - 0.15},${lat + 0.15},${lng - 0.15},${lng + 0.15}`;
      const { data } = await venuesApi.getVenues({ page: 1, limit: 12, bounds });
      set({ nearbyVenues: data.map(venueToConciergeVenue), nearbyLoading: false });
    } catch {
      set({ nearbyVenues: [], nearbyLoading: false });
    }
  },

  initMapCity: async () => {
    if (get().mapCity !== undefined) return;
    const coords = await requestBrowserLocation();
    if (!coords) {
      set({ mapCity: null });
      return;
    }
    const name = await reverseGeocodeCity(coords.lat, coords.lng);
    // A manual pick via the city picker while this was resolving takes priority.
    if (get().mapCity !== undefined) return;
    // Geolocation succeeded even if the reverse-geocode name lookup didn't — keep the real
    // coordinates (so the map still centers/marks the user's actual position) rather than
    // discarding them to null just because we couldn't label them with a city name.
    set({ mapCity: { name: name ?? 'Current location', lat: coords.lat, lng: coords.lng } });
    void get().loadNearbyVenues(coords.lat, coords.lng);
  },

  loadPersistedHistory: async () => {
    if (get().historyLoaded) return;
    set({ historyLoaded: true });
    const persisted = await fetchConciergeHistory();
    if (persisted.length === 0) return;
    // A greeting or a message already sent while this was in flight takes priority —
    // don't clobber a conversation the user's already started.
    if (get().messages.length > 0) return;
    set({
      messages: persisted.map((m) => ({ id: makeId(), role: m.role, content: m.content })),
    });
  },

  ensureGreeting: (character) => {
    set((state) => {
      if (state.messages.length > 0) return state;
      return {
        messages: [
          {
            id: makeId(),
            role: 'assistant',
            content: GREETINGS[character || 'default'] || GREETINGS.default,
          },
        ],
      };
    });
  },

  setCharacter: (character) => set({ character }),

  setHoveredVenue: (id) => set({ hoveredVenueId: id }),

  setActiveTab: (tab) => set({ activeTab: tab }),

  loadVenueTagChips: async () => {
    if (get().venueTagChips !== null || get().venueTagChipsLoading) return;
    set({ venueTagChipsLoading: true });
    try {
      const allTags = await venuesApi.getAllVenueTags();
      set({ venueTagChips: allTags.venue ?? [], venueTagChipsLoading: false });
    } catch {
      set({ venueTagChips: [], venueTagChipsLoading: false });
    }
  },

  selectExploreCategory: async (tagId) => {
    set({ exploreCategoryId: tagId, exploreLoading: true });
    try {
      const venues =
        tagId === null
          ? (await venuesApi.getVenues({ page: 1, limit: 12 })).data
          : await venuesApi.filterVenues({ venue: [tagId] });
      // A slower tab switch/category click landing after a newer one would otherwise
      // overwrite fresher results — only commit if this is still the active selection.
      if (get().exploreCategoryId !== tagId) return;
      set({ exploreVenues: venues.map(venueToConciergeVenue), exploreLoading: false });
    } catch {
      if (get().exploreCategoryId !== tagId) return;
      set({ exploreVenues: [], exploreLoading: false });
    }
  },

  sendMessage: async (text) => {
    const trimmed = text.trim();
    if (!trimmed || get().isSending) return;

    const { messages, character } = get();
    const history: ConciergeMessage[] = messages
      .filter((m) => !m.isError)
      .map((m) => ({ role: m.role, content: m.content }));

    const userMessage: ConciergeChatMessage = { id: makeId(), role: 'user', content: trimmed };
    const assistantId = makeId();
    set((state) => ({
      messages: [
        ...state.messages,
        userMessage,
        { id: assistantId, role: 'assistant', content: '', isStreaming: true },
      ],
      isSending: true,
    }));

    abortController?.abort();
    const controller = new AbortController();
    abortController = controller;

    const patchAssistant = (patch: Partial<ConciergeChatMessage>) => {
      set((state) => ({
        messages: state.messages.map((m) => (m.id === assistantId ? { ...m, ...patch } : m)),
      }));
    };

    // Lazy geolocation: only prompt the first time a message implies proximity, and only
    // once per session (guestLocation stays `undefined` until the first such attempt, then
    // becomes the resolved coords or `null` on denial/unavailability — never re-prompts).
    let location = get().guestLocation;
    if (location === undefined && NEAR_ME_PATTERN.test(trimmed)) {
      location = await requestBrowserLocation();
      set({ guestLocation: location });
    }

    // The "near me" keyword match above is just for the browser permission prompt timing —
    // it shouldn't gate whether we tell the backend where the user is. The map's already-known
    // location (mapCity, from geolocation-on-load or the city picker) is exactly the same signal
    // the backend needs to default to nearMe instead of asking; previously this only sent
    // coordinates when the message itself said "near me", so every other message looked
    // location-less to the AI even though the map clearly knew where the user was.
    const effectiveLocation = location ?? (get().mapCity ? { lat: get().mapCity!.lat, lng: get().mapCity!.lng } : null);
    const cityContext = get().mapCity?.name ?? null;

    await streamConciergeChat(
      {
        message: trimmed,
        history,
        character,
        lat: effectiveLocation?.lat ?? null,
        lng: effectiveLocation?.lng ?? null,
        cityContext,
      },
      {
        onDelta: (delta) => {
          const current = get().messages.find((m) => m.id === assistantId);
          patchAssistant({ content: (current?.content ?? '') + delta });
        },
        onVenues: (venues) => patchAssistant({ venues }),
        onDone: () => {
          patchAssistant({ isStreaming: false });
          set({ isSending: false });
        },
        onError: (message) => {
          patchAssistant({ content: message, isStreaming: false, isError: true });
          set({ isSending: false });
        },
      },
      controller.signal
    );
  },

  cancel: () => {
    abortController?.abort();
  },
}));
